/**
 * Disclosure completeness checklist generator.
 *
 * Not a per-row rulepack — produces a structured checklist of expected disclosures
 * given the entity's profile and what's present in their financial position.
 * Covers AASB 7, 12, 108, 110, 124, 137, 1054, 1060.
 */

export type Profile = 'sme' | 'nfp' | 'small_business' | 'listed_tier1';

export interface ChecklistContext {
  profile: Profile;
  hasLeases: boolean;
  hasInventory: boolean;
  hasShares: boolean;             // listed equity instruments
  hasFinancialInstruments: boolean;
  hasGrants: boolean;             // government / philanthropic
  hasRelatedParties: boolean;
  hasProvisions: boolean;
  hasGoodwill: boolean;
  hasSegments: boolean;
  hasForeignOps: boolean;
  hasShareBasedPayments: boolean;
  hasContingencies: boolean;
  changedAccountingPolicy: boolean;
  prepareGpfs: boolean;
}

export interface ChecklistItem {
  ref: string;          // e.g. "AASB 124.18"
  category: string;
  requirement: string;
  applicableTo: Profile[];
  conditional?: string;  // human description of when it applies
  status: 'required' | 'conditional' | 'recommended';
}

export function generateDisclosureChecklist(ctx: ChecklistContext): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  const all: Profile[] = ['sme', 'nfp', 'small_business', 'listed_tier1'];
  const gpfs: Profile[] = ['sme', 'nfp', 'listed_tier1'];

  // AASB 101 / 1060 / 18 — basic presentation
  if (ctx.prepareGpfs) {
    items.push({ ref: 'AASB 101.10 / AASB 1060.21', category: 'Presentation', requirement: 'Statement of profit or loss and other comprehensive income', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 101.10 / AASB 1060.21', category: 'Presentation', requirement: 'Statement of financial position', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 101.10 / AASB 1060.21', category: 'Presentation', requirement: 'Statement of changes in equity', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 107', category: 'Presentation', requirement: 'Statement of cash flows', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 101.117 / AASB 1060.30', category: 'Policies', requirement: 'Material accounting policy information (post-2024 wording — "material", not "significant")', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 101.122–125', category: 'Policies', requirement: 'Key accounting estimates and judgements specific to the entity', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 1054.7', category: 'AU-specific', requirement: 'Statement of compliance with Australian Accounting Standards', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 1054.8', category: 'AU-specific', requirement: 'Audit fees disclosure', applicableTo: gpfs, status: 'required' });
  }

  // AASB 18 (effective Jan 2027) — early warning
  if (ctx.profile === 'listed_tier1') {
    items.push({ ref: 'AASB 18 (Jan 2027)', category: 'Future standard', requirement: 'AASB 18 transition planning: P&L category restructure (operating / investing / financing), management-defined performance measures (MPMs), aggregation/disaggregation principles', applicableTo: ['listed_tier1'], status: 'recommended' });
    items.push({ ref: 'AASB S1 / S2', category: 'Sustainability', requirement: 'Climate-related and broader sustainability disclosures (phased mandatory from FY26 for large entities)', applicableTo: ['listed_tier1'], status: 'required', conditional: 'Group 1 entities from periods beginning 1 January 2025 onwards' });
  }

  // AASB 107
  items.push({ ref: 'AASB 107.43', category: 'Cash flow', requirement: 'Reconciliation between cash and cash equivalents in CF and BS', applicableTo: gpfs, status: 'required' });

  // AASB 108
  if (ctx.changedAccountingPolicy) {
    items.push({ ref: 'AASB 108.28', category: 'Policy change', requirement: 'Title of standard / nature of change / impact on prior periods (restatement)', applicableTo: all, status: 'required', conditional: 'Voluntary or mandatory accounting policy change' });
  }

  // AASB 110
  items.push({ ref: 'AASB 110.21', category: 'Subsequent events', requirement: 'Date the financial statements were authorised for issue and who gave authorisation', applicableTo: gpfs, status: 'required' });
  items.push({ ref: 'AASB 110.10', category: 'Subsequent events', requirement: 'Disclosure of non-adjusting events that are material', applicableTo: all, status: 'conditional', conditional: 'When material non-adjusting events exist between reporting date and authorisation' });

  // AASB 116
  items.push({ ref: 'AASB 116.73', category: 'PPE', requirement: 'Useful lives or depreciation rates by class; reconciliation of carrying amount opening to closing', applicableTo: gpfs, status: 'required' });

  // AASB 16 leases
  if (ctx.hasLeases) {
    items.push({ ref: 'AASB 16.51–60', category: 'Leases', requirement: 'Lessee disclosures: depreciation by class of underlying asset, interest expense, total cash outflow, ROU asset additions', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 16.58', category: 'Leases', requirement: 'Maturity analysis of lease liabilities (undiscounted)', applicableTo: gpfs, status: 'required' });
  }

  // AASB 102
  if (ctx.hasInventory) {
    items.push({ ref: 'AASB 102.36', category: 'Inventories', requirement: 'Carrying amount split by classification appropriate to entity (e.g. raw materials, WIP, finished)', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 102.36(e)', category: 'Inventories', requirement: 'Amount of any write-down recognised as expense', applicableTo: gpfs, status: 'required' });
  }

  // AASB 7 / 9
  if (ctx.hasFinancialInstruments) {
    items.push({ ref: 'AASB 7.31', category: 'Financial instruments', requirement: 'Risk disclosures: credit, liquidity, market — qualitative + quantitative', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 7.35A–H', category: 'Financial instruments', requirement: 'Expected credit loss disclosures (loss allowance reconciliation, inputs, write-offs)', applicableTo: gpfs, status: 'required' });
  }

  // AASB 124
  if (ctx.hasRelatedParties) {
    items.push({ ref: 'AASB 124.13', category: 'Related parties', requirement: 'Parent and ultimate controlling party (regardless of transactions)', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 124.17', category: 'Related parties', requirement: 'KMP compensation: short-term, post-employment, other long-term, termination, share-based — categorised aggregate', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 124.18', category: 'Related parties', requirement: 'Nature of relationship + amount + outstanding balances + bad debt expense for transactions', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 124.18A', category: 'Related parties', requirement: 'Australian-specific: KMP individual disclosures may be additionally required for listed entities under Corp Act s300A', applicableTo: ['listed_tier1'], status: 'required' });
  }

  // AASB 137
  if (ctx.hasProvisions) {
    items.push({ ref: 'AASB 137.84', category: 'Provisions', requirement: 'For each class: opening, additions, used, unused reversed, unwinding of discount, closing', applicableTo: gpfs, status: 'required' });
    items.push({ ref: 'AASB 137.85', category: 'Provisions', requirement: 'Brief description, expected timing, uncertainties, expected reimbursements', applicableTo: gpfs, status: 'required' });
  }
  if (ctx.hasContingencies) {
    items.push({ ref: 'AASB 137.86', category: 'Contingent liabilities', requirement: 'For each class: brief description + estimate of financial effect + uncertainties + possibility of reimbursement', applicableTo: gpfs, status: 'required' });
  }

  // AASB 12
  if (ctx.profile !== 'small_business' && (ctx.hasSegments || ctx.profile === 'listed_tier1')) {
    items.push({ ref: 'AASB 12.10–11', category: 'Group structure', requirement: 'Significant judgements + composition of group + interests in subsidiaries / NCI', applicableTo: ['sme', 'listed_tier1'], status: 'conditional', conditional: 'Where group has subsidiaries / associates / joint arrangements' });
  }

  // AASB 8
  if (ctx.hasSegments) {
    items.push({ ref: 'AASB 8.20–24', category: 'Segments', requirement: 'Reportable segment information: revenue by external/intersegment, profit/loss measure, assets, liabilities (where reviewed by CODM)', applicableTo: ['listed_tier1', 'sme'], status: 'required' });
    items.push({ ref: 'AASB 8.32', category: 'Segments', requirement: 'Information about products / services revenue', applicableTo: ['listed_tier1'], status: 'required' });
    items.push({ ref: 'AASB 8.33', category: 'Segments', requirement: 'Geographic information: revenue + non-current assets by country / Australia + foreign', applicableTo: ['listed_tier1'], status: 'required' });
    items.push({ ref: 'AASB 8.34', category: 'Segments', requirement: 'Major customers (≥10% of revenue) — disclose existence and segment, not customer name', applicableTo: ['listed_tier1', 'sme'], status: 'required' });
  }

  // AASB 121
  if (ctx.hasForeignOps) {
    items.push({ ref: 'AASB 121.52', category: 'Foreign exchange', requirement: 'Net exchange differences in OCI / P&L; reconciliation of foreign currency translation reserve', applicableTo: gpfs, status: 'required' });
  }

  // AASB 2
  if (ctx.hasShareBasedPayments) {
    items.push({ ref: 'AASB 2.44–52', category: 'Share-based payment', requirement: 'Description of arrangements + fair-value measurement basis + expense recognised + outstanding awards', applicableTo: gpfs, status: 'required' });
  }

  // AASB 133
  if (ctx.hasShares && ctx.profile === 'listed_tier1') {
    items.push({ ref: 'AASB 133.66–69', category: 'EPS', requirement: 'Basic and diluted EPS on the face of P&L; reconciliation of weighted average shares; description of dilutive instruments', applicableTo: ['listed_tier1'], status: 'required' });
  }

  // NFP-specific
  if (ctx.profile === 'nfp') {
    if (ctx.hasGrants) {
      items.push({ ref: 'AASB 1058.30 / AASB 15', category: 'NFP income', requirement: 'Distinguish AASB 1058 immediate-recognition income from AASB 15 contract liabilities; movement in unspent restricted funds', applicableTo: ['nfp'], status: 'required' });
    }
    items.push({ ref: 'ACNC governance', category: 'NFP governance', requirement: 'Compliance with ACNC governance standards 1–6; conflict-of-interest register; responsible persons (board) details', applicableTo: ['nfp'], status: 'required' });
  }

  return items.filter(it => it.applicableTo.includes(ctx.profile));
}
